import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
import { Button } from "../components/ui/button";
import {
  CheckCircle2,
  Music2,
  Scale,
  GraduationCap,
  Headphones,
  RotateCw,
  Brain,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  Star,
  Clock,
  XCircle,
  BookOpen,
  Gift,
  Loader2,
  Mail,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

/**
 * LandingPageLavender — White / Purple homepage matching the user's mockup.
 * Toggled live from Admin → Site Config → Theme. The legacy LandingPage.jsx is
 * unchanged so admins can flip back instantly.
 */
export default function LandingPageLavender() {
  const navigate = useNavigate();
  const [sample, setSample] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  // Live subscription pricing — populated from /api/subscription/plans so this strip stays in sync
  const [subPricing, setSubPricing] = useState({ display: 9.99, foundingActive: false });
  const audioRef = useRef(null);

  // Free song lead-capture state
  const [leadEmail, setLeadEmail] = useState("");
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  const handleFreeSongSubmit = async (e) => {
    e.preventDefault();
    const email = leadEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/leads/free-song`, { email, source: "landing_hero" });
      setLeadSubmitted(true);
      toast.success("Check your inbox — your free song is on its way!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLeadSubmitting(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/sample`)
      .then((res) => setSample(res.data))
      .catch(() => {});
    axios
      .get(`${API}/subscription/plans`)
      .then((res) => {
        const founding = res.data?.founding_member_status?.available;
        const deal = res.data?.deal;
        const byId = res.data?.plans_by_id || {};
        const fp = byId.founding_monthly?.price ?? 5.99;
        const rp = byId.unlimited_monthly?.price ?? 9.99;
        const display = founding ? (deal?.active ? deal.deal_price : fp) : rp;
        setSubPricing({ display, foundingActive: !!founding });
      })
      .catch(() => {});
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioPlaying) {
      audioRef.current.pause();
      setAudioPlaying(false);
    } else {
      audioRef.current.play();
      setAudioPlaying(true);
    }
  };

  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    setAudioProgress(audioRef.current.currentTime);
  };

  const onLoadedMetadata = () => {
    if (!audioRef.current) return;
    setAudioDuration(audioRef.current.duration || 90);
  };

  const fmtTime = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  return (
    <div className="bg-background text-foreground" data-testid="landing-lavender">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Soft music-notes background flourish */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.06] select-none">
          <svg
            className="absolute -left-12 top-24 w-[520px]"
            viewBox="0 0 200 100"
            fill="none"
          >
            <path
              d="M10 70 Q60 20, 120 50 T 190 30"
              stroke="currentColor"
              strokeWidth="1"
              className="text-primary"
            />
            <path
              d="M10 85 Q70 40, 140 70 T 195 55"
              stroke="currentColor"
              strokeWidth="1"
              className="text-primary"
            />
          </svg>
          <svg
            className="absolute right-0 bottom-12 w-[640px]"
            viewBox="0 0 200 100"
            fill="none"
          >
            <path
              d="M10 50 Q70 10, 140 40 T 195 25"
              stroke="currentColor"
              strokeWidth="1"
              className="text-primary"
            />
          </svg>
        </div>

        <div className="container mx-auto px-6 lg:px-12 pt-12 pb-16 lg:pt-20 lg:pb-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div data-testid="hero-copy">
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-5">
                <span className="text-black">Law School,</span>{" "}
                <span className="text-primary italic">Remixed.</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl">
                Bar Songz tunes black letter law concepts into music you can actually remember.
              </p>

              <ul className="space-y-3 mb-10">
                {[
                  "Created by a singer/songwriter — and a non-traditional law student",
                  "Built for retention — black-letter law, set to music your brain replays",
                  "Supplement dense outlines with hooks that stick",
                  "Listen anywhere — in the car, at the gym, between classes",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-foreground/90">{item}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById("who-this-is-for");
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="group inline-flex flex-col items-start gap-2 text-left text-primary hover:text-primary/80 transition-colors"
                data-testid="hero-scroll-cue"
                aria-label="Keep scrolling to learn more"
              >
                <span className="text-xs font-bold uppercase tracking-[0.2em]">
                  Keep scrolling
                </span>
                <span className="inline-flex items-center gap-2 text-sm font-medium">
                  See why it&apos;s built for you
                  <ChevronDown
                    className="h-5 w-5 transition-transform group-hover:translate-y-1 motion-safe:animate-bounce"
                    aria-hidden="true"
                  />
                </span>
              </button>

              <div className="mt-10 flex items-center gap-3 text-sm">
                <span className="text-muted-foreground">
                  Trusted by law students who are serious about results.
                </span>
                <span className="inline-flex items-center gap-0.5 text-primary">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </span>
                <span className="text-muted-foreground">4.9/5 from early listeners</span>
              </div>
            </div>

            {/* Hero image — Bar Songz brand: purple soundwave + scales of justice + music notes */}
            <div className="relative" data-testid="hero-image-wrap">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-secondary/40">
                <img
                  src="/hero-bar-songz.png"
                  alt="Purple soundwave flowing into a balance scale with floating music notes — Bar Songz, where law meets music."
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
                {/* Subtle vignette for harmony with surrounding page background */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, hsl(var(--background) / 0.10) 0%, transparent 40%, transparent 70%, hsl(var(--background) / 0.30) 100%)",
                  }}
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* WHO THIS IS FOR — mirror block, lives directly before the founder bio
          so the visitor recognizes themselves before reading Nye's story. */}
      <section id="who-this-is-for" className="border-t border-border bg-secondary/30 scroll-mt-24">
        <div className="container mx-auto px-6 lg:px-12 py-14 lg:py-20 max-w-4xl">
          <div className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">
              Built For You
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold leading-tight">
              This is for you if&hellip;
            </h2>
          </div>

          <ul className="space-y-4 max-w-2xl mx-auto" data-testid="who-its-for-list">
            {[
              "You're a non-traditional law student who works, parents, commutes long distances to school, or all three — and need to study in motion.",
              "Flashcards put you to sleep and outlines have stopped working.",
              "You learn better when something clicks than when you grind.",
              "You are a law student drowning in reading but have limited time.",
              "You refuse to fall behind just because your life isn't a 9-to-5 study schedule.",
            ].map((line, i) => (
              <li
                key={i}
                className="flex items-start gap-4 group"
                data-testid={`who-its-for-item-${i}`}
              >
                <span
                  className="mt-2 h-2 w-2 rounded-full bg-primary flex-shrink-0 transition-transform group-hover:scale-150"
                  aria-hidden="true"
                />
                <p className="text-base md:text-lg text-foreground/85 leading-relaxed font-serif">
                  {line}
                </p>
              </li>
            ))}
          </ul>

          <div className="text-center mt-10">
            <p className="text-foreground/70 text-sm md:text-base italic">
              If even one of these sounds like you, you&apos;re in the right place.
            </p>
          </div>
        </div>
      </section>


      {/* BUILT BY EXPERIENCE */}
      <section id="about-the-founder" className="border-t border-border scroll-mt-24">
        <div className="container mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {/* Brand image of the founder */}
            <div className="lg:col-span-3">
              <div className="relative aspect-square rounded-xl overflow-hidden border border-border shadow-sm">
                <img
                  src="https://customer-assets.emergentagent.com/job_6199349c-fdd5-42f1-ab34-3369e90dd605/artifacts/sstkul3i_m3.frontfacing.jpg"
                  alt="Nye — founder of Bar Songz"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Copy */}
            <div className="lg:col-span-5">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-5">
                Built by <span className="text-primary">Experience</span>.
                <br />
                Powered by <span className="text-primary">Music</span>.
              </h2>
              <p className="text-foreground/80 mb-4 leading-relaxed">
                I&apos;m <span className="font-serif italic text-primary">Nye</span> — singer/songwriter,
                legal professional<sup className="text-primary">*</sup>, mom, commuter, and non-traditional
                law student.
              </p>
              <p className="text-foreground/80 mb-4 leading-relaxed">
                Success in law school assumes you have unlimited time. But if you&apos;re like me, you don&apos;t.
              </p>
              <p className="text-foreground/80 mb-4 leading-relaxed">
                Between work, family, and a 3-hour commute to school&nbsp;—&nbsp;
                <em>in one direction</em>&nbsp;—&nbsp;I needed a way to study doctrine when I
                couldn&apos;t read hundreds of pages of material or sit with an outline. And one day
                it struck me: why not set black-letter law concepts to music?
              </p>
              <p className="text-foreground/80 mb-4 leading-relaxed">
                Music is one of the first ways we ever learned as children — who among us doesn&apos;t
                know their ABCs thanks to a song? And considering law school is essentially
                teaching us baby, soon-to-be-lawyers material unlike anything we&apos;ve ever learned
                before, why not lean back into what worked for us, well&hellip; as babies?
              </p>
              <p className="text-foreground/80 mb-4 leading-relaxed">
                <strong>Bar Songz is the study tool I wish I&apos;d thought of during my 1L year.</strong>
                {" "}It&apos;s here now, for you — the law student working to retain massive amounts of
                black-letter law, determined not to let time get the best of them.
              </p>
              <p className="text-primary italic font-medium mb-6 leading-relaxed">
                Here&apos;s to studying in the grooves of a full life.
              </p>
            </div>

            {/* Credentials */}
            <div className="lg:col-span-4 space-y-6">
              {[
                {
                  icon: Music2,
                  title: "Musician",
                  body: "Trained singer/songwriter with years of performance and songwriting experience.",
                },
                {
                  icon: Scale,
                  title: "Legal Professional",
                  body: "20+ years of hands-on experience in the legal profession before becoming a law student.",
                },
                {
                  icon: GraduationCap,
                  title: "Law Student",
                  body: "Living it every day. Building what I needed to succeed.",
                },
              ].map((c) => (
                <div key={c.title} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <c.icon className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">{c.title}</h3>
                    <p className="text-sm text-foreground/75 leading-relaxed">{c.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-t border-border bg-secondary/30">
        <div className="container mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-12">
            How It <span className="border-b-2 border-primary pb-1">Works</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-10 md:gap-6 max-w-5xl mx-auto relative">
            {/* Dotted connector line - desktop only */}
            <div
              className="hidden md:block absolute top-12 left-[16.66%] right-[16.66%] border-t-2 border-dashed border-primary/30"
              aria-hidden="true"
            />
            {[
              {
                n: "1",
                icon: Headphones,
                title: "Listen",
                body: "Structured songs break down complex legal concepts.",
              },
              {
                n: "2",
                icon: RotateCw,
                title: "Repeat",
                body: "Repetition through music helps lock it in.",
              },
              {
                n: "3",
                icon: Brain,
                title: "Remember",
                body: "Recall rules the way you remember your favorite hooks.",
              },
            ].map((s) => (
              <div key={s.n} className="relative flex flex-col items-center text-center">
                <div className="relative z-10 mb-4">
                  <span className="absolute -top-2 -left-2 h-7 w-7 rounded-full bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center">
                    {s.n}
                  </span>
                  <div className="h-24 w-24 rounded-full border-2 border-primary bg-background flex items-center justify-center">
                    <s.icon className="h-10 w-10 text-primary" />
                  </div>
                </div>
                <h3 className="font-serif text-xl font-semibold mb-1">{s.title}</h3>
                <p className="text-sm text-muted-foreground max-w-[220px]">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NOT FOR CRAMMING — sets expectation about how Bar Songz is meant to be used */}
      <section className="border-t border-border bg-primary/[0.04]" data-testid="not-for-cramming-band">
        <div className="container mx-auto px-6 lg:px-12 py-12 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">
              <span className="text-primary text-base align-middle">♫</span>{" "}
              A quick <span className="font-serif italic text-primary">NOTE</span> about how this works.{" "}
              <span className="text-primary text-base align-middle">♫</span>
            </p>
            <p className="text-base md:text-lg leading-relaxed text-foreground max-w-2xl mx-auto">
              Bar Songz isn&rsquo;t a cramming tool. It&rsquo;s the{" "}
              <span className="text-primary font-semibold">soundtrack to your law school journey</span>{" "}
              &mdash; built to be absorbed on repeat, in the background of your real life, until the doctrine feels familiar by exam day.
            </p>
          </div>
        </div>
      </section>

      {/* PREVIEW + PLAYER + CTA */}
      <section className="border-t border-border">
        <div className="container mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid lg:grid-cols-3 gap-8 items-stretch">
            {/* Preview the Songs */}
            <div data-testid="preview-songs-block" className="text-center flex flex-col justify-center">
              <h3 className="font-serif text-2xl md:text-3xl font-bold mb-3">
                Preview <span className="text-primary">the Songs</span>
              </h3>
              <p className="text-muted-foreground mb-6">
                Hear what makes Bar Songz different.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate("/browse")}
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full px-6 h-11"
                data-testid="browse-songs-btn"
              >
                Browse Songs
              </Button>
            </div>

            {/* Audio player */}
            <div id="sample-player" className="bg-secondary/40 border border-border rounded-2xl p-7 shadow-sm scroll-mt-24 flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-1">
                <div className="h-16 w-16 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Music2 className="h-7 w-7 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground leading-none mb-1">
                    {sample?.sample_info?.course_name || "Civil Procedure"} &middot; {sample?.sample_info?.title || "Supplemental Jurisdiction"}
                  </p>
                  <p className="font-semibold text-base leading-tight truncate">
                    {sample?.sample_info?.song_title || '"Side-Piece"'}
                  </p>
                  <p className="text-xs text-muted-foreground">Bar Songz</p>
                </div>
              </div>

              {/* Progress */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                <span>{fmtTime(audioProgress)}</span>
                <div className="flex-1 h-1 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-[width] duration-200"
                    style={{
                      width: audioDuration
                        ? `${Math.min(100, (audioProgress / audioDuration) * 100)}%`
                        : "0%",
                    }}
                  />
                </div>
                <span>{fmtTime(audioDuration)}</span>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-4">
                <button
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Previous"
                  data-testid="player-prev-btn"
                  onClick={() => {
                    if (audioRef.current) audioRef.current.currentTime = 0;
                  }}
                >
                  <SkipBack className="h-5 w-5" />
                </button>
                <button
                  onClick={togglePlay}
                  className="h-12 w-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
                  aria-label={audioPlaying ? "Pause" : "Play"}
                  data-testid="player-play-btn"
                >
                  {audioPlaying ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5 ml-0.5" />
                  )}
                </button>
                <button
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Next"
                  data-testid="player-next-btn"
                >
                  <SkipForward className="h-5 w-5" />
                </button>
                <Volume2 className="h-4 w-4 text-muted-foreground ml-1" />
              </div>

              {sample?.sample_url && (
                <audio
                  ref={audioRef}
                  src={sample.sample_url.startsWith("http") ? sample.sample_url : `${BACKEND}${sample.sample_url}`}
                  onTimeUpdate={onTimeUpdate}
                  onLoadedMetadata={onLoadedMetadata}
                  onEnded={() => setAudioPlaying(false)}
                  preload="metadata"
                  className="hidden"
                />
              )}
            </div>

            {/* CTA panel — Free song lead capture */}
            <div className="bg-gradient-to-br from-primary/10 via-secondary to-primary/5 border border-primary/30 rounded-2xl p-7" data-testid="lead-magnet-panel">
              {!leadSubmitted ? (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                      <Gift className="h-3 w-3" /> Free Track
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl font-bold mb-3 leading-tight">
                    Hear a Full Song. <span className="text-primary italic">On Us.</span>
                  </h3>
                  <p className="text-foreground/80 mb-5 text-sm leading-relaxed">
                    Drop your email and we&apos;ll send you the complete MP3 of{" "}
                    <span className="font-semibold italic text-primary">
                      {sample?.sample_info?.song_title || '"Side-Piece"'}
                    </span>
                    — yours to keep. No credit card. No subscription. Just one song,
                    fully yours.
                  </p>
                  <form onSubmit={handleFreeSongSubmit} className="space-y-3" data-testid="lead-magnet-form">
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <input
                        type="email"
                        required
                        value={leadEmail}
                        onChange={(e) => setLeadEmail(e.target.value)}
                        placeholder="your.email@lawschool.edu"
                        disabled={leadSubmitting}
                        className="w-full pl-9 pr-3 py-3 rounded-full bg-background border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:opacity-60"
                        data-testid="lead-magnet-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={leadSubmitting}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-7 h-11 w-full font-medium"
                      data-testid="lead-magnet-submit-btn"
                    >
                      {leadSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Gift className="h-4 w-4 mr-2" />
                          Send Me My Free Song
                        </>
                      )}
                    </Button>
                    <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                      One email. One song. We&apos;ll occasionally send Bar Songz updates.
                      Unsubscribe anytime.
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center py-2" data-testid="lead-magnet-success">
                  <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/15 mb-4">
                    <CheckCircle2 className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold mb-2">Check Your Inbox</h3>
                  <p className="text-foreground/80 text-sm mb-5">
                    Your free song is on its way to <strong>{leadEmail}</strong>.
                    If you don&apos;t see it within a minute, check your spam folder.
                  </p>
                  <Button
                    onClick={() => navigate("/packs")}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-7 h-11"
                    data-testid="lead-magnet-explore-packs-btn"
                  >
                    Explore More Songs
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING STRIP — moved below Preview Songs row */}
      <section className="border-t border-border bg-secondary/30" data-testid="pricing-strip">
        <div className="container mx-auto px-6 lg:px-12 py-8 lg:py-10">
          <div className="text-center mb-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">Pricing</p>
            <h2 className="font-serif text-2xl md:text-3xl font-bold">At a glance</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 items-stretch">
            {/* Stream Unlimited */}
            <button
              onClick={() => navigate("/subscribe")}
              className="group flex items-center justify-between gap-4 p-4 rounded-xl border border-border bg-background hover:border-primary/60 hover:shadow-sm transition-all text-left"
              data-testid="price-card-subscription"
            >
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                  Subscription
                </p>
                <p className="font-serif text-lg font-semibold leading-tight">
                  Stream Unlimited
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-xl text-primary leading-none">${subPricing.display}</p>
                <p className="text-[11px] text-muted-foreground mt-1">/month{subPricing.foundingActive ? " · founding" : ""}</p>
              </div>
            </button>

            {/* Buy Credits — pay-as-you-go streams */}
            <button
              onClick={() => navigate("/buy-credits")}
              className="group flex items-center justify-between gap-4 p-4 rounded-xl border border-border bg-background hover:border-primary/60 hover:shadow-sm transition-all text-left"
              data-testid="price-card-credits"
            >
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                  Pay-As-You-Go
                </p>
                <p className="font-serif text-lg font-semibold leading-tight">
                  20 Song Streams
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-xl text-primary leading-none">$10</p>
                <p className="text-[11px] text-muted-foreground mt-1">one-time</p>
              </div>
            </button>

            {/* Pick Any 4 */}
            <button
              onClick={() => navigate("/packs")}
              className="group flex items-center justify-between gap-4 p-4 rounded-xl border border-border bg-background hover:border-primary/60 hover:shadow-sm transition-all text-left"
              data-testid="price-card-any4"
            >
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                  Download · Play Forever
                </p>
                <p className="font-serif text-lg font-semibold leading-tight">
                  Pick Any 4 Songs
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-xl text-primary leading-none">$25</p>
                <p className="text-[11px] text-muted-foreground mt-1">one-time</p>
              </div>
            </button>

            {/* Course Pack - Best Value */}
            <button
              onClick={() => navigate("/packs")}
              className="relative group flex items-center justify-between gap-4 p-4 rounded-xl border-2 border-primary bg-primary/5 hover:bg-primary/10 transition-all text-left"
              data-testid="price-card-course"
            >
              <span className="absolute -top-2 left-4 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                Best Value
              </span>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                  Download · Play Forever
                </p>
                <p className="font-serif text-lg font-semibold leading-tight">
                  Course Pack · 6 Songs
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-xl text-primary leading-none">$30</p>
                <p className="text-[11px] text-muted-foreground mt-1">one-time</p>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border" data-testid="faq-section">
        <div className="container mx-auto px-6 lg:px-12 py-14 lg:py-20 max-w-3xl">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">FAQs</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold leading-tight">
              Good questions, <span className="text-primary italic">straight answers.</span>
            </h2>
          </div>
          <div className="space-y-3">
            {[
              {
                q: "Are these AI-generated?",
                a: "Yes \u2014 and also no. The audio is produced with AI tools, but these are not the kind of songs you get from dumping outlines or class notes into a model and pressing go. Every track starts with carefully curated black-letter doctrine, which Nye \u2014 a singer, songwriter, and performer with years of collective music industry experience and training, and a bachelor's degree in music \u2014 shapes into lyrics, melody, harmony, and song structure designed to make the productions memorable and the legal concepts stick. The result is craft first, AI second: songs you can replay in your head, not generic content.",
              },
              {
                q: "Are these summaries?",
                a: "Not really. While a summary aims to compress legal concepts into short paragraphs that you read, Bar Songz translates those same legal concepts into structured songs, with memorable lyrics and choruses \u2014 hooks your brain can replay while driving, working out at the gym, in the shower, or walking to class. Think SchoolHouse Rock, but for law school.",
              },
              {
                q: "Are these mnemonics?",
                a: "Sort of \u2014 but bigger. Traditional mnemonics give you an acronym to memorize. Bar Songz gives you a whole melody, lyric, and rhythm built around the rule, so the underlying concept (not just the acronym) sticks.",
              },
              {
                q: "Do I need a subscription?",
                a: "Nope. You can subscribe for unlimited streaming, OR buy a Course Pack ($30 for 6 songs) or Pick Any 4 ($25). The download options give you MP3s you keep on your device \u2014 no recurring charge.",
              },
            ].map((item, i) => (
              <details
                key={i}
                className="group rounded-xl border border-border bg-card hover:border-primary/40 transition-colors overflow-hidden"
                data-testid={`faq-item-${i}`}
              >
                <summary
                  className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-3 font-serif text-base md:text-lg font-semibold select-none"
                  data-testid={`faq-question-${i}`}
                >
                  <span>{item.q}</span>
                  <span
                    aria-hidden="true"
                    className="flex-shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full bg-primary/10 text-primary text-lg leading-none transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                <div className="px-5 pb-5 -mt-1 text-sm md:text-base text-muted-foreground leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING UPSELL — second-chance for visitors who scrolled past the top pricing strip */}
      <section className="border-t border-border bg-gradient-to-b from-background to-secondary/30">
        <div className="container mx-auto px-6 lg:px-12 py-14 lg:py-20 max-w-5xl text-center">
          <p
            className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2"
            data-testid="closing-upsell-leadin"
          >
            From the first beat to the bar — we&apos;ve got you.
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold leading-tight mb-3">
            Pick the way that <span className="text-primary italic">fits your style.</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-10 text-base">
            Subscribe for unlimited streaming of the whole library, or buy a pack and own
            the tracks forever — your call.
          </p>

          <div className="grid md:grid-cols-2 gap-4 md:gap-5 max-w-3xl mx-auto text-left">
            {/* Subscribe card */}
            <button
              type="button"
              onClick={() => navigate("/subscribe")}
              className="group relative flex flex-col p-6 rounded-2xl border border-primary/30 bg-card hover:border-primary hover:shadow-md transition-all"
              data-testid="closing-upsell-subscribe-card"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                  <Headphones className="h-3 w-3" /> Subscription
                </span>
                <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
                  Cancel anytime &rarr;
                </span>
              </div>
              <h3 className="font-serif text-2xl font-bold leading-tight mb-2">
                Stream Unlimited
              </h3>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                Listen to every track in every course. New songs added regularly. No limits.
              </p>
              <div className="mt-auto flex items-end justify-between">
                <div>
                  <p className="text-3xl font-bold text-primary leading-none">
                    ${subPricing.display}
                    <span className="text-sm font-normal text-muted-foreground ml-1">/month</span>
                  </p>
                  {subPricing.foundingActive && (
                    <p className="text-[11px] text-amber-500 mt-1 font-medium">
                      Founding rate · normally $9.99
                    </p>
                  )}
                </div>
                <span className="text-sm font-semibold text-primary group-hover:translate-x-1 transition-transform">
                  Subscribe &rarr;
                </span>
              </div>
            </button>

            {/* Buy Songs card */}
            <button
              type="button"
              onClick={() => navigate("/packs")}
              className="group relative flex flex-col p-6 rounded-2xl border-2 border-primary bg-primary/5 hover:bg-primary/10 hover:shadow-md transition-all"
              data-testid="closing-upsell-packs-card"
            >
              <span className="absolute -top-2.5 left-6 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-primary text-primary-foreground">
                Most Popular
              </span>
              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-2.5 py-1 rounded-full">
                  <Music2 className="h-3 w-3" /> Play Forever
                </span>
                <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
                  No subscription &rarr;
                </span>
              </div>
              <h3 className="font-serif text-2xl font-bold leading-tight mb-2">
                Buy Course Packs
              </h3>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                Download MP3s you keep on your devices. Pick any 4 ($25) or grab a full
                Course Pack of 6 ($30).
              </p>
              <div className="mt-auto flex items-end justify-between">
                <div>
                  <p className="text-3xl font-bold text-primary leading-none">
                    from $25
                    <span className="text-sm font-normal text-muted-foreground ml-1">one-time</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Yours forever — no recurring charge
                  </p>
                </div>
                <span className="text-sm font-semibold text-primary group-hover:translate-x-1 transition-transform">
                  Browse Songs &rarr;
                </span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* PURPLE TRUST STRIP */}
      <section className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 lg:px-12 py-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-6 text-sm">
            {[
              { icon: GraduationCap, label: "Made for Law Students" },
              { icon: Brain, label: "Built for Retention" },
              { icon: Clock, label: "Save Time. Study Smarter." },
              { icon: XCircle, label: "Cancel Anytime" },
            ].map((t) => (
              <div key={t.label} className="flex items-center gap-2 justify-center md:justify-start">
                <t.icon className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium">{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
